import { Page } from 'common-front/src/components/page/page';
import { FilterColumns } from 'common-front/src/filtersColumns/filtersColumns';
import {
    executeVolunteerQuery,
    executeVolunteersQuery,
    useVolunteersSegmentsQuery
} from 'common-front/src/generated/graphqlHooks';
import { useHeavent } from 'common-front/src/hooks/useHeavent';
import { useSlugToCustomField } from 'common-front/src/hooks/useSlugToCustomField';
import { RichTableList } from 'common-front/src/richTable/richTableList';
import { VolunteerCells } from 'common-front/src/volunteers/list/volunteerCells';
import {
    CustomFieldPrivacy,
    PositionId,
    PositionsCategoryId,
    VolunteerRegistrationFragment
} from 'common/src/generated/types';
import { useService } from 'common/src/util/dependencies/dependencies';
import { DEFAULT_PAGINATION_LIMIT } from 'common/src/vo/pagination';
import { VolunteersRendering } from 'common/src/vo/rendering';
import {
    columnsToIncludes,
    VolunteerDefaultColumns,
    VolunteersSegmentsService
} from 'common/src/vo/segments/volunteersSegmentsService';
import * as React from 'react';
import { useEventContext } from '../../../events/show/eventContext';
import { useLocalEventState } from '../../../hooks/useLocalState';
import { VolunteersActions } from '../../../volunteers/list/volunteersActions';

interface IPositionMembersProps {
    positionCategoryId?: PositionsCategoryId;
    positionId?: PositionId;
    preassignedPositionCategoryId?: PositionsCategoryId;
    preassignedPositionId?: PositionId;
}

export const PositionMembers = (props: IPositionMembersProps) => {
    const {
        params: { organizationId, eventId }
    } = useHeavent();
    const volunteersSegmentsService = useService(VolunteersSegmentsService);
    const { isEventAdmin } = useEventContext();
    const [columns, setColumns] = useLocalEventState('positionMembers.columns', [
        VolunteerDefaultColumns.Name,
        VolunteerDefaultColumns.Positions
    ]);
    const [limit, setLimit] = useLocalEventState('positionMembers.limit', DEFAULT_PAGINATION_LIMIT);
    const [predicates, setPredicates] = useLocalEventState('positionMembers.predicates', []);
    const [sort, setSort] = useLocalEventState('positionMembers.sort', null);
    const { data } = useVolunteersSegmentsQuery({
        organizationId,
        eventId,
        privacy: isEventAdmin ? CustomFieldPrivacy.All : CustomFieldPrivacy.Public,
        loadForms: false,
        loadSegments: false
    });
    const customFields = React.useMemo(
        () => data.organization?.customFields.nodes ?? [],
        [data.organization]
    );
    const slugToCustomField = useSlugToCustomField(customFields);
    const filters = React.useMemo(() => {
        if (data.event) {
            return volunteersSegmentsService.getPositionMembersFilters(
                organizationId,
                eventId,
                customFields,
                data.event
            );
        } else {
            return [];
        }
    }, [eventId, customFields, data.event]);
    const possibleColumns = React.useMemo(
        () => volunteersSegmentsService.getPositionMembersPossibleColumns(customFields),
        [customFields]
    );

    return (
        <Page.Content>
            <FilterColumns
                columns={columns}
                columnsSearch={[]}
                columnsToIncludes={columnsToIncludes}
                eventId={eventId}
                filters={filters}
                getMassActionId={(row: VolunteerRegistrationFragment) => row.userInfo.id}
                limit={limit}
                loadDataList={executeVolunteersQuery}
                loadDataListParams={{
                    includeCanRegistrationStateUpdate: !isEventAdmin,
                    includeIsEditable: !isEventAdmin,
                    positionCategoryId: props.positionCategoryId,
                    positionId: props.positionId,
                    preassignedPositionCategoryId: props.preassignedPositionCategoryId,
                    preassignedPositionId: props.preassignedPositionId
                }}
                loadRow={executeVolunteerQuery}
                loadRowParams={{
                    includeCanRegistrationStateUpdate: !isEventAdmin,
                    includeIsEditable: !isEventAdmin
                }}
                organizationId={null}
                possibleColumns={possibleColumns}
                predicates={predicates}
                renderActions={(massActions, totalCount, reload) => (
                    <VolunteersActions
                        massActions={massActions}
                        predicates={null}
                        showOnlyMassEdit={true}
                        totalNumberOfMembers={totalCount}
                        onMassEditSuccess={reload}
                    />
                )}
                rendering={VolunteersRendering.List}
                setColumns={setColumns}
                setLimit={setLimit}
                setPredicates={setPredicates}
                setSort={setSort}
                showEditColumns={isEventAdmin}
                showEditMode={isEventAdmin}
                showFilters={isEventAdmin}
                showMassActions={isEventAdmin}
                showRowDropdown={false}
                showRowEdit={false}
                showSearchColumns={false}
                sort={sort}
            >
                <RichTableList
                    renderCell={(column, row: VolunteerRegistrationFragment, reload) => (
                        <VolunteerCells
                            canAccredit={isEventAdmin}
                            canAssign={
                                isEventAdmin /* Better than no-one. Position leaders with edit rights will soon be able, too. */
                            }
                            canChangeDelegation={isEventAdmin}
                            canClickMemberName={true}
                            column={column}
                            customField={slugToCustomField.get(column.slug)}
                            eventId={eventId}
                            reloadRow={reload}
                            row={row}
                        />
                    )}
                />
            </FilterColumns>
        </Page.Content>
    );
};
